// ============================================
// FILE: src/components/CartScreen.jsx
// ============================================

import React, { useState } from 'react';
import { ShoppingCart, Plus, Minus, Trash2, Tag, X, Sparkles, ArrowLeft } from 'lucide-react';
import { getProductImage } from '../utils/imageHelpers';

const CartScreen = ({ 
  cart, 
  updateQuantity, 
  removeFromCart, 
  getCartTotal,
  getCartCount,
  handleCheckout,
  // Coupon props
  couponCode,
  setCouponCode,
  appliedCoupon,
  couponDiscount,
  couponError,
  handleApplyCoupon,
  handleRemoveCoupon,
  getFinalTotal,
  getSuggestedCoupons,
  // Settings
  appSettings = { taxRate: 5, freeShippingThreshold: 500, standardShippingFee: 50 },
  setActiveTab,
  goBack,
  appTheme = 'blue',
  isDesktop = false,
}) => {
  const [showCouponInput, setShowCouponInput] = useState(false);
  const suggestedCoupons = getSuggestedCoupons ? getSuggestedCoupons() : [];

  // Header style
  const headerStyle = appTheme === 'white'
    ? { background: '#fff', color: '#222', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }
    : { background: 'var(--header-gradient)', color: 'var(--text-light)' };

  return (
    <div className="pb-20 min-h-screen" style={{ background: 'var(--primary-bg)', color: 'var(--text-main)' }}>
      <div
        className={`p-6 ${isDesktop ? 'rounded-3xl' : 'rounded-b-3xl'}`}
        style={headerStyle}
      >
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={() => goBack ? goBack() : setActiveTab('home')}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
            title="Go Back"
            style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}
          >
            <ArrowLeft size={22} />
          </button>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}>Shopping Cart</h1>
            <p style={appTheme === 'white' ? { color: '#888' } : { color: 'var(--accent-light)' }}>{getCartCount()} items in cart</p>
          </div>
          {cart.length > 0 && (
            <div className="text-right">
              <p className="text-xs font-medium mb-0.5" style={appTheme === 'white' ? { color: '#888' } : { color: 'var(--accent-light)' }}>Cart Total</p>
              <p className="text-2xl font-bold" style={appTheme === 'white' ? { color: '#1d4ed8' } : { color: '#fff' }}>₹{getCartTotal()}</p>
            </div>
          )}
        </div>
      </div>

      {cart.length === 0 ? (
        <div className="flex flex-col items-center justify-center mt-20 px-6 text-center">
          <ShoppingCart size={64} style={{ color: 'var(--border)' }} className="mb-4" />
          <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-main)' }}>Your cart is empty</h3>
          <p style={{ color: 'var(--accent-light)' }}>Add some fresh dairy products to get started!</p>
        </div>
      ) : (
        <div className="p-4">
          {/* Total Savings Section (Top) */}
          {cart.length > 0 && (
            <div className="rounded-xl shadow-sm p-4 mb-4 flex items-center justify-between bg-green-50 border border-green-200">
              <span className="text-green-700 font-semibold text-base">
                Total Savings
              </span>
              <span className="text-green-700 font-bold text-lg">
                ₹{cart.reduce((total, item) => {
                  if (item.sale_price && item.sale_price < item.price) {
                    return total + ((item.price - item.sale_price) * item.quantity);
                  }
                  return total;
                }, 0).toFixed(2)}
              </span>
            </div>
          )}

          {cart.map(item => {
            const unitPrice = (item.sale_price && item.sale_price < item.price) ? Number(item.sale_price) : Number(item.price);
            const itemSubtotal = (unitPrice * item.quantity).toFixed(2);
            return (
              <div key={item.id} className="rounded-xl shadow-sm p-4 mb-3" style={{ background: 'var(--primary-bg-light)' }}>
                <div className="flex items-center">
                  <div className="p-3 rounded-lg mr-3 flex-shrink-0" style={{ background: 'var(--primary-bg)', opacity: 0.8 }}>
                    {getProductImage(item, 'medium')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate" style={{ color: 'var(--text-main)' }}>{item.name}</h3>
                    {(item.weight || item.unit) && (
                      <p className="text-xs" style={{ color: 'var(--accent)' }}>
                        {item.weight ? `${item.weight}` : ''}{item.unit ? ` ${item.unit}` : ''}
                      </p>
                    )}
                    {item.sale_price && item.sale_price < item.price ? (
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold" style={{ color: 'var(--danger)' }}>₹{item.sale_price} each</p>
                        <p className="text-xs line-through" style={{ color: 'var(--border)' }}>₹{item.price}</p>
                      </div>
                    ) : (
                      <p className="text-sm" style={{ color: 'var(--text-main)' }}>₹{item.price} each</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <button
                      onClick={() => updateQuantity(item.id, -1)}
                      className="p-1 rounded-lg"
                      style={{ background: 'var(--border)', color: 'var(--text-main)' }}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="font-semibold w-8 text-center" style={{ color: 'var(--text-main)' }}>
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, 1)}
                      className="p-1 rounded-lg"
                      style={{ background: 'var(--border)', color: 'var(--text-main)' }}
                    >
                      <Plus size={16} />
                    </button>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="ml-2 p-1 rounded-lg"
                      style={{ background: 'var(--danger)', color: 'var(--text-light)' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                {/* Item subtotal row */}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                  <span className="text-xs" style={{ color: 'var(--accent)' }}>
                    {item.quantity} × ₹{unitPrice.toFixed(2)}
                  </span>
                  <span className="text-sm font-bold" style={{ color: 'var(--text-main)' }}>
                    ₹{itemSubtotal}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Coupon Section */}
          <div className="rounded-xl shadow-sm p-4 mt-4" style={{ background: 'var(--primary-bg-light)' }}>
            {!appliedCoupon ? (
              <>
                <button
                  onClick={() => setShowCouponInput(!showCouponInput)}
                  className="flex items-center gap-2 text-blue-600 font-semibold mb-3"
                >
                  <Tag size={20} />
                  <span>Apply Coupon</span>
                </button>

                {showCouponInput && (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={handleApplyCoupon}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-all"
                      >
                        Apply
                      </button>
                    </div>

                    {couponError && (
                      <div className="text-red-500 text-sm flex items-center gap-1">
                        <X size={16} />
                        <span>{couponError}</span>
                      </div>
                    )}

                    {/* Suggested Coupons */}
                    {suggestedCoupons.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                          <Sparkles size={16} className="text-yellow-500" />
                          <span>Available Coupons</span>
                        </p>
                        <div className="space-y-2">
                          {suggestedCoupons.map((coupon) => (
                            <div
                              key={coupon.code}
                              onClick={() => {
                                setCouponCode(coupon.code);
                                handleApplyCoupon();
                              }}
                              className="border border-dashed border-blue-300 rounded-lg p-3 cursor-pointer hover:bg-blue-50 transition-all"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <p className="font-bold text-blue-600">{coupon.code}</p>
                                  <p className="text-xs text-gray-600">{coupon.description}</p>
                                </div>
                                <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                                  {coupon.type === 'percentage' ? `${coupon.discount}%` : `₹${coupon.discount}`} OFF
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="bg-green-50 border border-green-300 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Tag size={20} className="text-green-600" />
                    <div>
                      <p className="font-bold text-green-700">{appliedCoupon.code}</p>
                      <p className="text-sm text-green-600">You saved ₹{couponDiscount}!</p>
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveCoupon}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Free Shipping Motivation Message */}
          {(() => {
            const cartTotal = getCartTotal() - (couponDiscount || 0);
            const remaining = appSettings.freeShippingThreshold - cartTotal;
            
            if (cartTotal >= appSettings.freeShippingThreshold) {
              return (
                <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-400 rounded-xl p-4 mt-6 flex items-center gap-3">
                  <div className="text-3xl">🎉</div>
                  <div className="flex-1">
                    <p className="font-bold text-green-700">You qualify for FREE shipping!</p>
                    <p className="text-sm text-green-600">No delivery charges on this order</p>
                  </div>
                </div>
              );
            } else if (remaining > 0) {
              return (
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-400 rounded-xl p-4 mt-6 flex items-center gap-3">
                  <div className="text-3xl">🚚</div>
                  <div className="flex-1">
                    <p className="font-bold text-blue-700">Add ₹{remaining.toFixed(0)} more for FREE shipping!</p>
                    <p className="text-sm text-blue-600">Save ₹{appSettings.standardShippingFee} on delivery charges</p>
                  </div>
                </div>
              );
            }
          })()}

          {/* Total Savings Section (Bottom) */}
          {cart.length > 0 && (
            <div className="rounded-xl shadow-sm p-4 mb-4 flex items-center justify-between bg-green-50 border border-green-200">
              <span className="text-green-700 font-semibold text-base">
                Total Savings
              </span>
              <span className="text-green-700 font-bold text-lg">
                ₹{cart.reduce((total, item) => {
                  if (item.sale_price && item.sale_price < item.price) {
                    return total + ((item.price - item.sale_price) * item.quantity);
                  }
                  return total;
                }, 0).toFixed(2)}
              </span>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm p-4 mt-6">
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-semibold">₹{getCartTotal()}</span>
            </div>
            {appliedCoupon && (
              <div className="flex justify-between mb-2">
                <span className="text-green-600">Coupon Discount ({appliedCoupon.code})</span>
                <span className="font-semibold text-green-600">-₹{couponDiscount}</span>
              </div>
            )}
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Shipping</span>
              <span className="font-semibold">
                {getCartTotal() >= appSettings.freeShippingThreshold ? (
                  <span className="text-green-600">Free</span>
                ) : (
                  `₹${appSettings.standardShippingFee}`
                )}
              </span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Tax (GST {appSettings.taxRate}%)</span>
              <span className="font-semibold">₹{(getCartTotal() * (appSettings.taxRate / 100)).toFixed(2)}</span>
            </div>
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between">
                <span className="text-lg font-bold">Total</span>
                <span className="text-lg font-bold text-blue-600">₹{getFinalTotal ? getFinalTotal() : getCartTotal()}</span>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-xl font-semibold mt-4 hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center gap-2">
              <span>Proceed to Checkout</span>
              <span className="bg-white bg-opacity-20 px-3 py-0.5 rounded-full text-sm font-bold">
                ₹{getFinalTotal ? getFinalTotal() : getCartTotal()}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartScreen;